import { Dispatch, SetStateAction, useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, FullProject } from 'entities';

import { Column, Row, Alert, Skeleton } from 'components/Blocks';
import { SortFilterButtons } from 'components/Buttons';
import { ListManipulation } from 'components/ListManipulation';

import { LoadMoreButton } from './LoadMoreButton';
import { LoadConfig } from './LoadConfig';
import SingleProject from '../SingleProject';

import { useFormControl } from 'hooks';
import { useUser } from 'hooks/user';

import { formatDate } from 'utils/date';
import { omit } from 'utils/object';
import { getBaseQuery } from './utils';
import { getProjectFilters } from '../filters';

import { ActiveTabProjectCountControl, ProjectTab } from '..';
import { LoadConfigState } from './types';

interface ProjectByStatusProps {
  tab: ProjectTab;
  setActiveTabProjectCount: Dispatch<SetStateAction<ActiveTabProjectCountControl>>;
}

const tabToStatus: Record<
  ProjectTab,
  { timeProperty: keyof FullProject; textKey: string; showSearch: boolean }
> = {
  Completed: { timeProperty: 'completedAt', textKey: 'done-at', showSearch: true },
  Canceled: { timeProperty: 'canceledAt', textKey: 'canceled-at', showSearch: true },
  Unassigned: { timeProperty: 'createdAt', textKey: 'created-at', showSearch: false },
  inProgress: { timeProperty: 'createdAt', textKey: 'created-at', showSearch: true },
};

export function ProjectByStatus({ tab, setActiveTabProjectCount }: ProjectByStatusProps) {
  const { t } = useLang();
  const user = useUser();

  const { textKey, timeProperty, showSearch } = tabToStatus[tab];

  const { dispatch, state } = useFormControl<LoadConfigState>({
    ref: user.team.canAssumeProject ? 'OWN' : 'STATUS',
    startDate: null,
    endDate: null,
    team: null,
    projects: [],
  });

  const timeParser = useCallback(
    (project: FullProject) => {
      return `${t(textKey)} ${formatDate(project[timeProperty] as string)}`;
    },
    [timeProperty, textKey, t],
  );

  const updateProjectList = ({
    projects,
    paginationToken,
  }: Pick<LoadConfigState, 'projects' | 'paginationToken'>) => {
    setActiveTabProjectCount({ loading: false, length: projects.length });

    dispatch({
      paginationToken,

      projects,
    });
  };

  const { loading } = loaders.projects.listProjectsByStatus.use({
    params: getBaseQuery(tab, omit(state, ['paginationToken'])),

    onDataChange: updateProjectList,
  });

  const saveNewLoadConfig = (config: LoadConfigState) => {
    const noChanges = Object.entries(config).every(
      ([key, value]) => value === state[key as keyof LoadConfigState],
    );

    if (noChanges) return;

    const updatedConfig = omit(config, ['paginationToken']);

    dispatch({
      ...updatedConfig,
      projects: [],
    });
  };

  if (loading) return <Skeleton height={110} quantity={6} />;

  return (
    <ListManipulation itens={state.projects} filters={getProjectFilters()}>
      {({ openFilters, parsedItens, hasFilter }) => (
        <Column relative>
          {showSearch && (
            <Row justifyContent="flex-end" gap="2" className="absolute -top-12 right-0">
              <SortFilterButtons gap="1" hasFilter={hasFilter} onFilterClick={openFilters} />

              <LoadConfig state={state} onSave={saveNewLoadConfig} />
            </Row>
          )}

          {!state.projects.length && (
            <Row justifyContent="center">
              <Alert level="info" title={t('no-project-found')} className="w-[80vw] max-w-[800px]">
                {t('as-soon-as-they-are-created')}
              </Alert>
            </Row>
          )}

          {parsedItens.map((project) => (
            <SingleProject key={project.id} project={project} timeParser={timeParser} />
          ))}

          <LoadMoreButton tab={tab} setProjects={updateProjectList} state={state} />
        </Column>
      )}
    </ListManipulation>
  );
}
